import { CommonModule } from '@angular/common';
import { Component, Host, NgModule, OnInit, Optional } from '@angular/core';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { TextPlaceholderModule } from '@app/components/text-placeholder/text-placeholder.component';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { HideParentIfEmptyDirective } from '@app/directives/hide-parent-if-empty.directive';
import { Restaurant, RestaurantMenu } from '@app/models/restaurant-menu';
import { LinkModule } from '@app/directives/link.directive';
import { RestaurantMenuService } from '@app/services/restaurant-menu.service';
import { RestaurantService } from '@app/services/restaurant.service';
import { UserChannelTermsService } from '@app/services/user-channel-terms.service';
import { endOfISOWeek, getISOWeek, startOfISOWeek } from 'date-fns';
import { BehaviorSubject, EMPTY, forkJoin, Observable } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-restaurant-menu',
  templateUrl: './restaurant-menu.component.html',
  styleUrls: ['./restaurant-menu.component.scss']
})
export class RestaurantMenuComponent implements OnInit {

  private changeSubject = new BehaviorSubject<number>(0);
  selectedRestaurant: number;
  restaurants$: Observable<Restaurant[]>;
  restaurantMenus$: Observable<RestaurantMenu[]>;
  currentWeekNumber: number;
  startOfISOWeek: Date;
  endOfISOWeek: Date;

  constructor(private restaurantService: RestaurantService,
              private userChannelTermsService: UserChannelTermsService,
              private lunchMenuService: RestaurantMenuService,
              @Host() @Optional() private hideParentIfEmptyDirective: HideParentIfEmptyDirective) { }

  handleSelection(event: MatSelectChange) {
    this.changeSubject.next(event.value);
  }

  ngOnInit() {
    // we treat week number according to ISO-8601 https://date-fns.org/docs/getISOWeek
    this.currentWeekNumber = getISOWeek(new Date());
    this.startOfISOWeek = startOfISOWeek(new Date());
    this.endOfISOWeek = endOfISOWeek(new Date());

    this.restaurants$ = forkJoin(
      [
        this.restaurantService.getAllRestaurants(),
        this.userChannelTermsService.getUserChannelTerms()
      ])
      .pipe(
        map(([restaurants, userChannels]) => {
          return restaurants.map(restaurant => {
            restaurant.UserChannelMatched = restaurant.ChannelTermRestaurants.some(restaurantChannel => userChannels.some(userChannel => userChannel.ChannelTermId === restaurantChannel.ChannelTermId));
            return restaurant;
          });
        }),
        tap(restaurants => {
          if (restaurants && restaurants.length > 0) {
            const matchedRestaurant = restaurants.find(restaurant => restaurant.UserChannelMatched) ?? restaurants.find(restaurant => restaurant.ChannelTermRestaurants.length === 0) ?? restaurants[0];
            this.selectedRestaurant = matchedRestaurant.Id;
            this.changeSubject.next(this.selectedRestaurant);
            this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.showElement();
          } else {
            this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.hideElement();
          }
        }));

    this.restaurantMenus$ = this.changeSubject
      .pipe(
        mergeMap(id => {
          if (id > 0) {
            return this.lunchMenuService.getCurrentRestaurantMenusForRestaurant(id);
          } else {
            return EMPTY;
          }
        }));
  }
}
@NgModule({
  declarations: [RestaurantMenuComponent],
  exports: [RestaurantMenuComponent],
  imports: [
    CommonModule,
    MatSelectModule,
    TextPlaceholderModule,
    WidgetPlaceholderModule,
    LinkModule,
  ],
})
export class RestaurantMenuModule { }
